/* ==========================================================================
   map.js — MapLibre GL + OpenStreetMap live store finder
   --------------------------------------------------------------------------
   Stack (keyless; endpoints swappable in config for paid tiers):
     · Render   : MapLibre GL + OpenFreeMap vector tiles
     · Geocoding: Nominatim          (postcode/ZIP → lat,lng)
     · Stores   : Overpass API        (partner-chain locations from OSM,
                  matched by `brand` so the query stays fast & accurate)
     · Distance + ETA : OSRM          (driving road distance + drive time for
                  the closest stores, in one table call)

   Flow: user origin → Overpass nearby brand match → sort by straight-line
   distance → take the N closest → OSRM for road distance + ETA → pins + list.
   Runs ONLY on user action. window.NATY.maps interface unchanged.
   ========================================================================== */

(function () {
  const { SETTINGS, brandPattern } = window.NATY;

  let map = null;
  let markers = [];
  const cache = new Map();

  /* --- Lazy-load MapLibre GL (≈800 KB) only when the map is first needed ---
     The page is built for Meta ad traffic, so we keep the renderer out of the
     initial download and fetch it on first finder use (or warm it on intent
     via preload()). Idempotent: the in-flight promise is reused. */
  const MAPLIBRE_JS = 'https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl.js';
  const MAPLIBRE_CSS = 'https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl.css';
  let maplibrePromise = null;
  function loadMapLibre() {
    if (window.maplibregl) return Promise.resolve();
    if (maplibrePromise) return maplibrePromise;
    maplibrePromise = new Promise((resolve, reject) => {
      const css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = MAPLIBRE_CSS;
      document.head.appendChild(css);
      const s = document.createElement('script');
      s.src = MAPLIBRE_JS;
      s.onload = () => resolve();
      s.onerror = () => { maplibrePromise = null; reject(new Error('maplibre-load-failed')); };
      document.head.appendChild(s);
    });
    return maplibrePromise;
  }

  /* --- Init the MapLibre map once --- */
  function ensureMap(center) {
    if (map) return map;
    map = new maplibregl.Map({
      container: 'map',
      style: SETTINGS.mapStyle,
      center: [center.lng, center.lat],
      zoom: 10,
      attributionControl: true,
      cooperativeGestures: true, // don't hijack page scroll on mobile
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    return map;
  }

  function clearMarkers() {
    markers.forEach((m) => m.remove());
    markers = [];
  }

  function makeMarker(pos, isOrigin, popupHtml) {
    const el = document.createElement('div');
    el.className = 'naty-pin' + (isOrigin ? ' naty-pin--me' : '');
    el.innerHTML = '<span class="naty-pin__dot"></span>';
    const m = new maplibregl.Marker({ element: el }).setLngLat([pos.lng, pos.lat]);
    if (popupHtml) m.setPopup(new maplibregl.Popup({ offset: 16, closeButton: false }).setHTML(popupHtml));
    return m;
  }

  /* Grey "other" stores live in a clustered GeoJSON source so dense areas
     collapse into a single count bubble instead of a wall of dots. */
  function setOthers(features) {
    const data = { type: 'FeatureCollection', features };
    const apply = () => {
      const src = map.getSource('naty-others');
      if (src) { src.setData(data); return; }
      map.addSource('naty-others', {
        type: 'geojson', data, cluster: true, clusterRadius: 46, clusterMaxZoom: 15,
      });
      // Cluster bubbles (grey, sized by count)
      map.addLayer({
        id: 'naty-clusters', type: 'circle', source: 'naty-others', filter: ['has', 'point_count'],
        paint: {
          'circle-color': '#8c958f',
          'circle-stroke-color': '#fff', 'circle-stroke-width': 2,
          'circle-radius': ['step', ['get', 'point_count'], 13, 10, 17, 50, 22],
        },
      });
      map.addLayer({
        id: 'naty-cluster-count', type: 'symbol', source: 'naty-others', filter: ['has', 'point_count'],
        layout: { 'text-field': ['get', 'point_count_abbreviated'], 'text-font': ['Noto Sans Regular'], 'text-size': 12 },
        paint: { 'text-color': '#fff' },
      });
      // Single (unclustered) grey dots
      map.addLayer({
        id: 'naty-unclustered', type: 'circle', source: 'naty-others', filter: ['!', ['has', 'point_count']],
        paint: { 'circle-color': '#9aa3a0', 'circle-radius': 6, 'circle-stroke-color': '#fff', 'circle-stroke-width': 2 },
      });
      // Click a cluster to zoom in and expand it
      map.on('click', 'naty-clusters', (e) => {
        const f = map.queryRenderedFeatures(e.point, { layers: ['naty-clusters'] })[0];
        if (!f) return;
        map.getSource('naty-others').getClusterExpansionZoom(f.properties.cluster_id)
          .then((z) => map.easeTo({ center: f.geometry.coordinates, zoom: z }))
          .catch(() => {});
      });
      // Click a single dot to see the store name
      map.on('click', 'naty-unclustered', (e) => {
        const f = e.features[0];
        new maplibregl.Popup({ offset: 12, closeButton: false })
          .setLngLat(f.geometry.coordinates)
          .setHTML(`<strong>${f.properties.name}</strong><br>${f.properties.chain}`)
          .addTo(map);
      });
      const ptr = () => (map.getCanvas().style.cursor = 'pointer');
      const clr = () => (map.getCanvas().style.cursor = '');
      ['naty-clusters', 'naty-unclustered'].forEach((l) => {
        map.on('mouseenter', l, ptr); map.on('mouseleave', l, clr);
      });
    };
    if (map.isStyleLoaded()) apply();
    else map.once('load', apply);
  }

  function haversine(a, b) {
    const R = 6371;
    const dLat = ((b.lat - a.lat) * Math.PI) / 180;
    const dLng = ((b.lng - a.lng) * Math.PI) / 180;
    const lat1 = (a.lat * Math.PI) / 180;
    const lat2 = (b.lat * Math.PI) / 180;
    const x = Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
    return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
  }

  /* --- Geocode a postcode/ZIP via Nominatim --- */
  async function geocode(query, region) {
    const url =
      'https://nominatim.openstreetmap.org/search?format=json&limit=1' +
      `&countrycodes=${encodeURIComponent(region.toLowerCase())}` +
      `&q=${encodeURIComponent(query)}`;
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('geocode-failed');
    const data = await res.json();
    if (!data.length) throw new Error('geocode-empty');
    return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
  }

  /* --- Overpass: match partner chains by brand (fast + accurate) --- */
  function overpassQuery(origin, chains, radius) {
    const alt = chains.map(brandPattern).join('|');
    return (
      `[out:json][timeout:25];` +
      `nwr[brand~"${alt}",i](around:${radius},${origin.lat},${origin.lng});` +
      `out center tags 120;`
    );
  }

  async function overpass(query) {
    let lastErr;
    for (const ep of SETTINGS.overpassEndpoints) {
      // Abort a hung mirror after overpassTimeoutMs so the loop falls through
      // to the next one fast — the [timeout:25] in the query is server-side
      // only and never fires when an endpoint stalls at the network level.
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), SETTINGS.overpassTimeoutMs || 8000);
      try {
        const res = await fetch(ep, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: 'data=' + encodeURIComponent(query),
          signal: ctrl.signal,
        });
        if (!res.ok) throw new Error('overpass-' + res.status);
        const json = await res.json();
        return json.elements || [];
      } catch (e) {
        lastErr = e;
      } finally {
        clearTimeout(timer);
      }
    }
    throw lastErr || new Error('overpass-failed');
  }

  function matchChain(tags, chains) {
    const hit = chains.find((c) => new RegExp(brandPattern(c), 'i').test(tags.brand || ''));
    return hit ? hit.name : null;
  }
  function elementLatLng(el) {
    if (el.lat != null && el.lon != null) return { lat: el.lat, lng: el.lon };
    if (el.center) return { lat: el.center.lat, lng: el.center.lon };
    return null;
  }
  function formatAddr(tags) {
    return [
      [tags['addr:housenumber'], tags['addr:street']].filter(Boolean).join(' '),
      tags['addr:city'] || tags['addr:suburb'] || tags['addr:postcode'],
    ].filter(Boolean).join(', ');
  }

  /* --- OSRM: road distance + drive time from origin to each point --- */
  async function osrm(origin, points) {
    const coords = [origin, ...points].map((p) => `${p.lng},${p.lat}`).join(';');
    const url =
      `${SETTINGS.osrmEndpoint}/table/v1/driving/${coords}` +
      `?sources=0&annotations=duration,distance`;
    const res = await fetch(url);
    if (!res.ok) throw new Error('osrm-failed');
    const data = await res.json();
    if (data.code !== 'Ok') throw new Error('osrm-' + data.code);
    return {
      durations: (data.durations && data.durations[0]) || [], // seconds, [origin, ...points]
      distances: (data.distances && data.distances[0]) || [], // meters
    };
  }

  /* --- Prebaked stores: load the per-market JSON once (lazy, cached). This is
     the fast path — an instant client-side haversine search, no live Overpass
     call. scripts/build-stores.mjs generates assets/data/stores-<code>.json. --- */
  const storeFiles = new Map();
  function loadStores(code) {
    if (storeFiles.has(code)) return storeFiles.get(code);
    const p = fetch(window.NATY_ASSETS.stores[code])
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => (d && Array.isArray(d.stores) ? d.stores : null))
      .catch(() => null); // missing/blocked → null → live Overpass fallback
    storeFiles.set(code, p);
    return p;
  }

  /* Live Overpass path — fallback used only when the prebaked file is absent
     (e.g. before the build script has run, or if the asset fails to load). */
  async function fetchStoresLive(origin, market) {
    const els = await overpass(
      overpassQuery(origin, market.physicalChains, SETTINGS.searchRadiusMeters)
    );
    // Build candidates (keep OSM type so we can pick a good representative).
    const candidates = [];
    els.forEach((el) => {
      const pos = elementLatLng(el);
      if (!pos || !el.tags) return;
      const chain = matchChain(el.tags, market.physicalChains);
      if (!chain) return;
      candidates.push({
        chain, name: el.tags.name || chain, address: formatAddr(el.tags),
        pos, isArea: el.type && el.type !== 'node', distanceKm: haversine(origin, pos),
      });
    });
    candidates.sort((a, b) => a.distanceKm - b.distanceKm);

    // Collapse the many OSM features of ONE physical store into a single entry:
    // same chain within ~250 m (a Walmart's Supercenter + Pharmacy + Garden
    // Center) is one store. Keep the best-labelled member. (The build script
    // does this same dedup ahead of time for the prebaked path.)
    const SUBDEPT = /pharmacy|apotheke|garden cent|petrol|fuel|gas station|caf[eé]|bakery|b[äa]ckerei|optician|photo|tyre|tire|travel money|kitchen|mobile|bank|geldautomat|atm|car ?park|parkpl/i;
    const repScore = (c) =>
      (c.address ? 2 : 0) + (c.isArea ? 1 : 0) +
      (c.name.trim().toLowerCase() === c.chain.toLowerCase() ? 1 : 0) +
      (SUBDEPT.test(c.name) ? -5 : 0);
    const clusters = [];
    candidates.forEach((c) => {
      const cl = clusters.find((k) => k.chain === c.chain && haversine(k.pos, c.pos) <= 0.25);
      if (cl) cl.members.push(c);
      else clusters.push({ chain: c.chain, pos: c.pos, members: [c] });
    });
    return clusters
      .map((cl) => {
        const rep = cl.members.slice()
          .sort((a, b) => repScore(b) - repScore(a) || a.distanceKm - b.distanceKm)[0];
        return {
          chain: cl.chain, name: rep.name, address: rep.address,
          pos: rep.pos, distanceKm: haversine(origin, rep.pos),
        };
      })
      .sort((a, b) => a.distanceKm - b.distanceKm);
  }

  /* Static-first: filter the prebaked file by radius (instant), else go live. */
  async function fetchStores(origin, market) {
    const data = await loadStores(market.code);
    if (data) {
      const radiusKm = SETTINGS.searchRadiusMeters / 1000;
      // config.js decides which chains are stockists; a store file baked before
      // a retailer was dropped must not keep surfacing that retailer's stores.
      const chains = new Set(market.physicalChains.map((c) => c.name));
      return data
        .filter((s) => chains.has(s.chain))
        .map((s) => {
          const pos = { lat: s.lat, lng: s.lng };
          return { chain: s.chain, name: s.name, address: s.address, pos, distanceKm: haversine(origin, pos) };
        })
        .filter((s) => s.distanceKm <= radiusKm)
        .sort((a, b) => a.distanceKm - b.distanceKm);
    }
    return fetchStoresLive(origin, market);
  }

  /* --- Public: full search around an origin --- */
  async function runSearch(origin, market, distanceUnit) {
    const cacheKey = `${market.code}:${origin.lat.toFixed(2)},${origin.lng.toFixed(2)}`;

    // Start the map (lazy MapLibre + tiles) IN PARALLEL with the store lookup and
    // drop the "you are here" pin immediately, so tiles paint during the wait
    // instead of a spinner over blank space. Non-fatal if WebGL is unavailable.
    const mapReady = loadMapLibre()
      .then(() => {
        ensureMap(origin);
        clearMarkers();
        const me = makeMarker(origin, true);
        me.addTo(map);
        markers.push(me);
        map.easeTo({ center: [origin.lng, origin.lat], zoom: 10, duration: 400 });
        return true;
      })
      .catch((e) => { console.warn('Map render unavailable; showing list only.', e); return false; });

    let stores = cache.get(cacheKey);
    if (!stores) {
      stores = await fetchStores(origin, market);

      // Real road distance + drive time, one OSRM call for the listed stores.
      const enrich = stores.slice(0, SETTINGS.listMax);
      try {
        const { durations, distances } = await osrm(origin, enrich.map((s) => s.pos));
        enrich.forEach((s, i) => {
          if (durations[i + 1] != null) s.driveMin = Math.max(1, Math.round(durations[i + 1] / 60));
          if (distances[i + 1] != null) s.roadKm = distances[i + 1] / 1000;
        });
      } catch (e) {
        enrich.forEach((s) => { s.driveMin = Math.max(1, Math.round((s.distanceKm * 1.3) / 40 * 60)); });
      }
      cache.set(cacheKey, stores);
    }

    // Add store pins once the map is ready (the origin pin is already showing).
    if (await mapReady) {
      try {
        const bounds = new maplibregl.LngLatBounds();
        bounds.extend([origin.lng, origin.lat]);
        const others = [];
        stores.slice(0, SETTINGS.maxMapPins).forEach((s, i) => {
          if (i < SETTINGS.closestCount) {
            // Featured: blue pin + popup, included in the fit.
            const m = makeMarker(s.pos, false, `<strong>${s.name}</strong><br>${s.chain}`);
            m.addTo(map);
            markers.push(m);
            bounds.extend([s.pos.lng, s.pos.lat]);
          } else {
            // Everything else → clustered grey source (count bubbles when dense).
            others.push({
              type: 'Feature',
              properties: { name: s.name, chain: s.chain },
              geometry: { type: 'Point', coordinates: [s.pos.lng, s.pos.lat] },
            });
          }
        });
        setOthers(others);
        if (stores.length) map.fitBounds(bounds, { padding: 50, maxZoom: 14, duration: 600 });
      } catch (mapErr) {
        console.warn('Map render unavailable; showing list only.', mapErr);
      }
    }

    // Shape rows: distance + a walk OR drive proposal.
    // Walking time is estimated from straight-line distance (driving distance
    // is unreliable on foot). < 15 min walk → propose a walk with the walking
    // distance; otherwise propose a drive with the road distance + drive time.
    const { walkLabel, driveLabel } = market.strings;
    const fmt = (km) =>
      distanceUnit === 'mi' ? `${(km * 0.621371).toFixed(1)} mi` : `${km.toFixed(1)} km`;
    return stores.slice(0, SETTINGS.listMax).map((s) => {
      const walkKm = s.distanceKm * 1.25; // straight-line → approx walking distance
      const walkMin = Math.max(1, Math.round((walkKm / 4.8) * 60));
      if (walkMin <= 15) {
        return { ...s, distanceLabel: fmt(walkKm), etaLabel: `${walkMin} ${walkLabel}` };
      }
      const driveKm = s.roadKm != null ? s.roadKm : s.distanceKm;
      const driveMin = s.driveMin != null ? s.driveMin : Math.max(1, Math.round((driveKm / 40) * 60));
      return { ...s, distanceLabel: fmt(driveKm), etaLabel: `${driveMin} ${driveLabel}` };
    });
  }

  /* --- Geolocation helper --- */
  function browserLocate() {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) return reject(new Error('no-geo'));
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => reject(new Error('geo-denied')),
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 }
      );
    });
  }

  /* Warm both heavy assets on user intent: the MapLibre bundle and the active
     market's prebaked store file. Both are idempotent + cached. */
  function preload(code) {
    if (code) loadStores(code);
    return loadMapLibre();
  }

  /* Market switched: drop the previous market's pins and recentre. */
  function reset(market) {
    if (!map) return;
    clearMarkers();
    if (map.getSource('naty-others')) setOthers([]);
    map.jumpTo({ center: [market.mapCenter.lng, market.mapCenter.lat], zoom: market.mapZoom });
  }

  window.NATY.maps = { runSearch, browserLocate, geocode, preload, reset };
})();
